import { TutorialDefinition } from '../TutorialEngine.js';
import { BranchesRenderOptions } from './BranchesTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type BranchesScene = {
    gameState: string;
    renderOptions: BranchesRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Full solution: R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E3E4,E4E5,D5E5,C5D5,C4C5,C4D4

const scenes: BranchesScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E3E4,E4E5,D5E5,C5D5,C4C5,C4D4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Branches, grow branches\nto reach all digits in order', cell: 'C1', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Placing stem, connect to digit 1
    {
        gameState: 'T3,D0D1,D1E1,E1E2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click rim edge\nto place stem', cell: 'B0', anchor: 'center'},
            { type: 'text-label', text: 'Click edges\nto extend path', cell: 'C2', anchor: 'center' },
            { type: 'pulse', edge: '3040', color: COLORS.teal },
            { type: 'pulse', edge: '4142', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                'L1',
                'R4',
                'T3',
                'T3,D0D1',
                'T3,D0D1,D1E1',
                'T3,D0D1,D1E1,E1E2'],
        },
    },

    // Scene 3: Path branches from digits, badges show distance
    {
        gameState: 'R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E3E4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Digits can branch', cell: 'E1', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Badges show\ndistance from stem', cell: 'A3', anchor: 'right', color: COLORS.teal },
        ],
        animate: {
            interval: 1000,
            loop: true,
            frames: [
                'R2,E2F2',
                'R2,E2F2,D2E2',
                'R2,E2F2,D2E2,C2D2',
                'R2,E2F2,D2E2,C2D2,B2C2',
                'R2,E2F2,D2E2,C2D2,B2C2,A2B2',
                'R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3',
                'R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3',
                'R2,E2F2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E3E4',
            ],
        },
    },

    // Scene 4: Path must not branch elsewhere, amber marks disconnected
    {
        gameState: 'R2,D2E2,C2D2,B2C2,E2E3,E2F2,E3E4,C2C3,A4B4,B3B4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Only digits\ncan branch', cell: 'C1', anchor: 'center', color: COLORS.red },
            { type: 'text-label', text: 'Amber = disconnected\nfrom stem', cell: 'A5', anchor: 'right', color: COLORS.amber },
            { type: 'pulse', cell: 'C2', color: COLORS.red },
        ],
    },

    // Scene 5: Digits must be reached in increasing order
    {
        gameState: 'R2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E2F2,E3E4,D4E4',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Digits must be reached\nin strict order', cell: 'C1', anchor: 'center', color: COLORS.amber },
            { type: 'pulse', cell: 'A3', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                'R2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E2F2,E3E4,D4E4',
                'R2,D2E2,C2D2,B2C2,A2B2,A2A3,E2E3,E2F2,E3E4,E4E5,D5E5,D4D5',
            ],
        },

    },

];

/**
 * Branches tutorial: 5 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/branches.md for detailed scene descriptions.
 */
export const BRANCHES_TUTORIAL: TutorialDefinition = {
    gameType: 'branches',
    gridCode: '6/1-3:1E2,2A3,3D4',
    scenes,
};
